'use client'

import Navbar from '@/components/Navbar'
import { Logo } from '@/components/Logo'
import HeroShowcase from '@/components/HeroShowcase'
import { useState, useEffect } from 'react'
import Link from 'next/link'
import { useTheme, ThemeToggle } from '@/components/ThemeProvider'

const garamondLink = 'https://fonts.googleapis.com/css2?family=Cormorant+Garamond:ital,wght@0,500;0,600;0,700;1,500;1,600&display=swap'
const serifStyle: React.CSSProperties = { fontFamily: "'Cormorant Garamond', Georgia, serif", fontWeight: 600 }
const serifLight: React.CSSProperties = { fontFamily: "'Cormorant Garamond', Georgia, serif", fontWeight: 500 }

const USE_CASES = [
  {
    id: 'sales',
    label: 'Sales',
    role: 'Sales Agent',
    name: 'Aria',
    color: '#166534',
    bg: '#f0fdf4',
    messages: [
      { from: 'user', text: 'Hi, I need pricing for 50 users' },
      { from: 'agent', text: 'Hi! For 50 users our Business plan is $199/mo — includes WhatsApp, Telegram, custom branding and priority support. Want a proposal sent to your email?' },
      { from: 'user', text: 'Yes please. Do you support Arabic?' },
      { from: 'agent', text: 'نعم بالتأكيد ✓ Aria speaks Arabic, English and 12 other languages. Proposal on its way!' },
    ],
  },
  {
    id: 'support',
    label: 'Support',
    role: 'Support Agent',
    name: 'Kira',
    color: '#1e3a5f',
    bg: '#eff6ff',
    messages: [
      { from: 'user', text: 'My order hasn\'t arrived after 5 days' },
      { from: 'agent', text: 'I\'m sorry to hear that! Let me look that up — can you share your order number?' },
      { from: 'user', text: 'ORD-28471' },
      { from: 'agent', text: 'Found it. Your package is at the local facility and will be delivered today before 6PM. I\'ve flagged it as priority for you.' },
    ],
  },
  {
    id: 'hr',
    label: 'HR',
    role: 'HR Agent',
    name: 'Zara',
    color: '#7c2d12',
    bg: '#fff7ed',
    messages: [
      { from: 'user', text: 'I want to apply for the marketing position' },
      { from: 'agent', text: 'Great! I\'ll guide you through the application. First, can you share your CV or LinkedIn profile?' },
      { from: 'user', text: 'Here\'s my LinkedIn: linkedin.com/in/ahmed' },
      { from: 'agent', text: 'Perfect, I\'ve reviewed your profile. You\'re a strong match — scheduling you with the hiring manager for Thursday at 10AM. Confirmation sent to your email.' },
    ],
  },
  {
    id: 'custom',
    label: 'Custom',
    role: 'Custom Agent',
    name: 'Your Agent',
    color: '#4c1d95',
    bg: '#f5f3ff',
    messages: [
      { from: 'user', text: 'What can your custom agent do?' },
      { from: 'agent', text: 'Anything you need — I can be trained on your products, policies, tone of voice and workflows. You define the role, I handle the conversations.' },
      { from: 'user', text: 'Can it handle Arabic customers?' },
      { from: 'agent', text: 'نعم — fully bilingual, available 24/7 on WhatsApp or Telegram. Deployed in hours, not weeks.' },
    ],
  },
]

const FEATURES = [
  { num: '01', title: 'WhatsApp & Telegram', desc: 'Deploys on the channels your customers already use. No app downloads, no friction.' },
  { num: '02', title: 'Bilingual Arabic + English', desc: 'Fluent in both languages. Switches automatically based on how the customer writes.' },
  { num: '03', title: 'Trained on your business', desc: 'Upload your products, FAQs, tone guide, and pricing. The agent knows your business inside out.' },
  { num: '04', title: '24/7 availability', desc: 'Never misses a message. Responds in under 2 seconds, any hour, any day.' },
  { num: '05', title: 'Handoff to human', desc: 'Knows when to escalate. Passes complex cases to your team with full conversation context.' },
  { num: '06', title: 'Analytics dashboard', desc: 'See every conversation, response rate, resolved queries, and customer satisfaction in real time.' },
]

const PRICING = [
  {
    name: 'Pro',
    price: '$240',
    period: '/month',
    desc: 'One AI agent, fully deployed',
    features: ['1 AI agent (your choice of role)', 'WhatsApp or Telegram', 'Bilingual Arabic + English', 'Custom training on your business', 'Analytics dashboard', '14-day free trial'],
    cta: 'Start Free Trial',
    href: '/signup?plan=agent-pro',
    highlight: false,
  },
  {
    name: 'Ultra',
    price: '$350',
    period: '/month',
    desc: 'Advanced agent with voice & memory',
    features: ['Everything in Pro', 'Voice mode (speaks to customers)', 'Long-term memory (remembers regulars)', 'Priority support & onboarding', 'Custom persona & branding', 'Dedicated account manager'],
    cta: 'Start Free Trial',
    href: '/signup?plan=agent-ultra',
    highlight: true,
  },
]

const FAQ = [
  { q: 'How is a Single Agent different from an AI Department?', a: 'A Single Agent is one focused AI employee — e.g. just a Sales Agent or just a Support Agent. The AI Department gives you a full team of 4+ agents across different roles, all working together under one plan.' },
  { q: 'How long does deployment take?', a: 'Most agents are live within hours. You fill out an onboarding form, we train the agent on your business, and it\'s connected to your WhatsApp or Telegram.' },
  { q: 'Do I need technical skills?', a: 'Zero. No code, no APIs, no setup. We handle everything. You just tell us what the agent should do.' },
  { q: 'Can I switch to an AI Department later?', a: 'Yes — your Single Agent plan upgrades seamlessly. All your training data and conversation history carries over.' },
  { q: 'What if the agent doesn\'t know the answer?', a: 'It escalates to a human on your team with the full conversation context. You can define exactly when and how handoffs happen.' },
]

export default function AIAgentPage() {
  const { theme } = useTheme()
  const [mounted, setMounted] = useState(false)
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false)
  const [activeTab, setActiveTab] = useState('sales')
  const [openFaq, setOpenFaq] = useState<number | null>(null)
  const [user, setUser] = useState<any>(null)

  useEffect(() => {
    setMounted(true)
    fetch('/api/profile').then(r => r.ok ? r.json() : null).then(d => { if (d?.id) setUser(d) }).catch(() => {})
  }, [])

  const activeCase = USE_CASES.find(c => c.id === activeTab) || USE_CASES[0]

  return (
    <div className={`min-h-screen bg-[#FAFAF8] dark:bg-gray-950 overflow-x-hidden transition-opacity duration-200 ${mounted ? 'opacity-100' : 'opacity-0'}`}>
      <link rel="stylesheet" href={garamondLink} />

      {/* ── Navbar ── */}
      <Navbar activePage="agent" />

      {/* ── Hero ── */}
      <section className="pt-24 sm:pt-32 pb-16 sm:pb-24 px-4 sm:px-6">
        <div className="max-w-7xl mx-auto grid md:grid-cols-2 gap-12 lg:gap-20 items-center">
          {/* Left */}
          <div>
            <div className="inline-block text-xs font-semibold tracking-widest uppercase text-gray-400 mb-6">Single AI Agent</div>
            <h1 style={serifStyle} className="text-5xl sm:text-6xl lg:text-7xl text-[#1a1a1a] dark:text-white leading-[1.06] mb-5">
              One agent.<br/>Any role.<br/><em className="italic text-gray-400">Deployed today.</em>
            </h1>
            <p className="text-base sm:text-lg text-gray-500 dark:text-gray-400 leading-relaxed mb-8 max-w-lg">
              Deploy a single AI agent trained on your business — Sales, Support, HR, or fully custom. Runs on WhatsApp or Telegram, speaks Arabic and English, available 24/7.
            </p>
            <div className="flex flex-wrap gap-3 items-center mb-10">
              <Link href="/login?redirect=/dashboard" className="bg-[#1a1a1a] dark:bg-white text-white dark:text-[#1a1a1a] px-7 py-3 rounded-full text-sm font-semibold hover:bg-black dark:hover:bg-gray-200 transition">
                Start Free Trial
              </Link>
              <a href="#how-it-works" className="text-sm text-gray-500 dark:text-gray-400 hover:text-[#1a1a1a] dark:hover:text-white transition flex items-center gap-1.5">
                See how it works <span className="text-lg leading-none">→</span>
              </a>
            </div>
            <div className="flex gap-8 pt-6 border-t border-[#E8E8E4] dark:border-gray-800">
              <div><div style={serifLight} className="text-3xl text-[#1a1a1a] dark:text-white">&lt;2s</div><div className="text-xs text-gray-400 mt-1">Response time</div></div>
              <div><div style={serifLight} className="text-3xl text-[#1a1a1a] dark:text-white">24/7</div><div className="text-xs text-gray-400 mt-1">Always on</div></div>
              <div><div style={serifLight} className="text-3xl text-[#1a1a1a] dark:text-white">0</div><div className="text-xs text-gray-400 mt-1">Lines of code</div></div>
            </div>
          </div>

          {/* Right — HeroShowcase */}
          <HeroShowcase />
        </div>
      </section>

      {/* ── Proof Bar ── */}
      <section className="py-5 px-4 border-y border-[#E8E8E4] dark:border-gray-800">
        <div className="max-w-5xl mx-auto flex items-center justify-center gap-8 sm:gap-14 flex-wrap">
          <span className="text-[11px] font-semibold tracking-widest uppercase text-gray-300 dark:text-gray-600">Powered by</span>
          {['Claude AI', 'WhatsApp Business API', 'Telegram', 'OpenAI', 'ElevenLabs Voice'].map(b => (
            <span key={b} className="text-xs font-semibold text-gray-400 dark:text-gray-500">{b}</span>
          ))}
        </div>
      </section>

      {/* ── Use Cases (Tabs) ── */}
      <section className="py-20 sm:py-28 px-4 sm:px-6">
        <div className="max-w-7xl mx-auto">
          <div className="mb-12">
            <div className="text-[11px] font-semibold tracking-widest uppercase text-gray-400 mb-3">Use Cases</div>
            <h2 style={serifStyle} className="text-4xl sm:text-5xl text-[#1a1a1a] dark:text-white mb-4">Your agent, any role</h2>
            <p className="text-gray-500 dark:text-gray-400 max-w-xl text-base leading-relaxed">Pick a pre-built role or go fully custom. Each agent is trained to handle real conversations in your industry.</p>
          </div>

          {/* Tab pills */}
          <div className="flex gap-2 flex-wrap mb-10">
            {USE_CASES.map(c => (
              <button
                key={c.id}
                onClick={() => setActiveTab(c.id)}
                className={`px-5 py-2 rounded-full text-sm font-medium transition-all ${activeTab === c.id ? 'bg-[#1a1a1a] dark:bg-white text-white dark:text-[#1a1a1a]' : 'bg-white dark:bg-gray-900 border border-[#E8E8E4] dark:border-gray-700 text-gray-600 dark:text-gray-300 hover:border-[#1a1a1a] dark:hover:border-white'}`}
              >
                {c.label}
              </button>
            ))}
          </div>

          <div className="grid md:grid-cols-2 gap-10 lg:gap-16 items-center">
            {/* Left: description */}
            <div>
              <div className="inline-block text-xs font-semibold px-3 py-1 rounded-full mb-5"
                style={{ background: activeCase.bg, color: activeCase.color }}>
                {activeCase.role}
              </div>
              <h3 style={serifStyle} className="text-3xl sm:text-4xl text-[#1a1a1a] dark:text-white mb-4">{activeCase.name}</h3>
              <p className="text-gray-500 dark:text-gray-400 text-base leading-relaxed mb-6">
                {activeTab === 'sales' && 'Qualifies leads, answers product questions, sends proposals, and follows up automatically — all on WhatsApp or Telegram. Your sales team only steps in to close.'}
                {activeTab === 'support' && 'Handles common customer questions, tracks orders, processes returns and escalates to humans when needed. Reduces support load by up to 80%.'}
                {activeTab === 'hr' && 'Screens job applicants, schedules interviews, answers policy questions, and guides new hires through onboarding — in Arabic and English.'}
                {activeTab === 'custom' && 'Define the role from scratch. Train it on your SOPs, product catalog, brand voice, and workflows. The agent becomes a true extension of your team.'}
              </p>
              <ul className="space-y-2.5 text-sm text-gray-500 dark:text-gray-400">
                {activeTab === 'sales' && ['Qualifies leads 24/7', 'Sends quotes and proposals', 'Follows up on cold leads', 'Books demos directly in chat'].map(f => (
                  <li key={f} className="flex items-center gap-2.5"><svg className="w-4 h-4 text-emerald-500 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}><path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7"/></svg>{f}</li>
                ))}
                {activeTab === 'support' && ['Resolves 80% of tickets automatically', 'Tracks orders in real time', 'Handles returns and refunds', 'Escalates with full context'].map(f => (
                  <li key={f} className="flex items-center gap-2.5"><svg className="w-4 h-4 text-emerald-500 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}><path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7"/></svg>{f}</li>
                ))}
                {activeTab === 'hr' && ['Screens CVs and shortlists candidates', 'Schedules interviews automatically', 'Onboards new hires step by step', 'Answers Saudi labor law questions'].map(f => (
                  <li key={f} className="flex items-center gap-2.5"><svg className="w-4 h-4 text-emerald-500 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}><path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7"/></svg>{f}</li>
                ))}
                {activeTab === 'custom' && ['Fully trained on your business', 'Custom persona and tone of voice', 'Integrates with your tools', 'You own all conversation data'].map(f => (
                  <li key={f} className="flex items-center gap-2.5"><svg className="w-4 h-4 text-emerald-500 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}><path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7"/></svg>{f}</li>
                ))}
              </ul>
            </div>

            {/* Right: phone */}
            <div className="flex justify-center">
              <div className="bg-[#111] rounded-[40px] p-3 shadow-2xl w-60" style={{ boxShadow: '0 32px 64px rgba(0,0,0,0.18), 0 0 0 1px rgba(255,255,255,0.04)' }}>
                <div className="absolute top-5 left-1/2 -translate-x-1/2 w-20 h-1.5 bg-[#333] rounded-full"></div>
                <div className="bg-white dark:bg-gray-900 rounded-[30px] overflow-hidden" style={{ height: 400 }}>
                  <div className="px-4 pt-7 pb-3 border-b flex items-center gap-3" style={{ background: activeCase.bg, borderColor: '#e5e7eb' }}>
                    <div className="w-8 h-8 rounded-full flex-shrink-0 flex items-center justify-center text-white text-xs font-bold"
                      style={{ background: activeCase.color }}>
                      {activeCase.name[0]}
                    </div>
                    <div>
                      <div className="text-xs font-semibold text-[#1a1a1a]">{activeCase.name} — {activeCase.role}</div>
                      <div className="flex items-center gap-1 mt-0.5">
                        <div className="w-1.5 h-1.5 rounded-full bg-green-500"></div>
                        <span className="text-[10px] text-gray-400">Online</span>
                      </div>
                    </div>
                  </div>
                  <div className="p-3 flex flex-col gap-2.5">
                    {activeCase.messages.map((m, i) => (
                      <div key={i} className={`text-xs leading-relaxed rounded-2xl px-3 py-2 max-w-[85%] ${m.from === 'user' ? 'bg-[#1a1a1a] text-white rounded-br-sm ml-auto' : 'bg-[#F0F0EB] text-[#1a1a1a] rounded-bl-sm'}`}>
                        {m.text}
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ── Features ── */}
      <section className="py-20 sm:py-28 px-4 sm:px-6 bg-white dark:bg-gray-900 border-y border-[#E8E8E4] dark:border-gray-800">
        <div className="max-w-7xl mx-auto">
          <div className="mb-14">
            <div className="text-[11px] font-semibold tracking-widest uppercase text-gray-400 mb-3">Capabilities</div>
            <h2 style={serifStyle} className="text-4xl sm:text-5xl text-[#1a1a1a] dark:text-white">Everything your agent needs</h2>
          </div>
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-px bg-[#E8E8E4] dark:bg-gray-800 rounded-2xl overflow-hidden">
            {FEATURES.map(f => (
              <div key={f.num} className="bg-white dark:bg-gray-900 p-7 hover:bg-[#FAFAF8] dark:hover:bg-gray-800 transition-colors">
                <div className="text-xs font-semibold text-gray-300 dark:text-gray-600 mb-4">{f.num}</div>
                <div className="text-sm font-semibold text-[#1a1a1a] dark:text-white mb-2">{f.title}</div>
                <div className="text-sm text-gray-500 dark:text-gray-400 leading-relaxed">{f.desc}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── How It Works ── */}
      <section id="how-it-works" className="py-20 sm:py-28 px-4 sm:px-6">
        <div className="max-w-4xl mx-auto">
          <div className="mb-14 text-center">
            <div className="text-[11px] font-semibold tracking-widest uppercase text-gray-400 mb-3">Process</div>
            <h2 style={serifStyle} className="text-4xl sm:text-5xl text-[#1a1a1a] dark:text-white">Live in three steps</h2>
          </div>
          <div className="grid md:grid-cols-3 gap-8">
            {[
              { num: '01', title: 'Choose your role', desc: 'Pick Sales, Support, HR, or describe a custom role. Tell us about your business and what the agent should handle.' },
              { num: '02', title: 'We train your agent', desc: 'Our team trains the agent on your products, policies, tone, and workflows. Usually done within 24 hours.' },
              { num: '03', title: 'Go live on WhatsApp', desc: 'Connect to your WhatsApp or Telegram number. Your agent starts handling conversations immediately.' },
            ].map(step => (
              <div key={step.num} className="text-center">
                <div className="text-4xl font-light text-gray-200 dark:text-gray-700 mb-4" style={serifLight}>{step.num}</div>
                <div className="text-base font-semibold text-[#1a1a1a] dark:text-white mb-2">{step.title}</div>
                <div className="text-sm text-gray-500 dark:text-gray-400 leading-relaxed">{step.desc}</div>
              </div>
            ))}
          </div>
          <div className="text-center mt-12">
            <Link href="/login?redirect=/dashboard" className="inline-block bg-[#1a1a1a] dark:bg-white text-white dark:text-[#1a1a1a] px-8 py-3.5 rounded-full text-sm font-semibold hover:bg-black dark:hover:bg-gray-200 transition">
              Get Started Today
            </Link>
          </div>
        </div>
      </section>

      {/* ── Pricing ── */}
      <section id="pricing" className="py-20 sm:py-28 px-4 sm:px-6 bg-white dark:bg-gray-900 border-y border-[#E8E8E4] dark:border-gray-800">
        <div className="max-w-4xl mx-auto">
          <div className="mb-14 text-center">
            <div className="text-[11px] font-semibold tracking-widest uppercase text-gray-400 mb-3">Pricing</div>
            <h2 style={serifStyle} className="text-4xl sm:text-5xl text-[#1a1a1a] dark:text-white mb-3">Simple, transparent pricing</h2>
            <p className="text-gray-500 dark:text-gray-400 text-sm">14-day free trial. No credit card required. Cancel anytime.</p>
          </div>
          <div className="grid md:grid-cols-2 gap-6">
            {PRICING.map(plan => (
              <div key={plan.name} className={`rounded-2xl p-8 border ${plan.highlight ? 'bg-[#1a1a1a] dark:bg-white border-[#1a1a1a] dark:border-white' : 'bg-[#FAFAF8] dark:bg-gray-950 border-[#E8E8E4] dark:border-gray-800'}`}>
                <div className={`text-xs font-semibold tracking-widest uppercase mb-2 ${plan.highlight ? 'text-gray-400' : 'text-gray-400'}`}>{plan.name}</div>
                <div className="flex items-baseline gap-1 mb-1">
                  <span style={serifStyle} className={`text-5xl ${plan.highlight ? 'text-white dark:text-[#1a1a1a]' : 'text-[#1a1a1a] dark:text-white'}`}>{plan.price}</span>
                  <span className={`text-sm ${plan.highlight ? 'text-gray-400' : 'text-gray-400'}`}>{plan.period}</span>
                </div>
                <p className={`text-sm mb-6 ${plan.highlight ? 'text-gray-400' : 'text-gray-500 dark:text-gray-400'}`}>{plan.desc}</p>
                <ul className="space-y-2.5 mb-8">
                  {plan.features.map(f => (
                    <li key={f} className={`flex items-center gap-2.5 text-sm ${plan.highlight ? 'text-gray-300 dark:text-gray-600' : 'text-gray-600 dark:text-gray-300'}`}>
                      <svg className={`w-4 h-4 flex-shrink-0 ${plan.highlight ? 'text-emerald-400' : 'text-emerald-500'}`} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}><path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7"/></svg>
                      {f}
                    </li>
                  ))}
                </ul>
                <Link href={plan.href} className={`block w-full text-center py-3 rounded-full text-sm font-semibold transition ${plan.highlight ? 'bg-white dark:bg-[#1a1a1a] text-[#1a1a1a] dark:text-white hover:bg-gray-100 dark:hover:bg-gray-800' : 'bg-[#1a1a1a] dark:bg-white text-white dark:text-[#1a1a1a] hover:bg-black dark:hover:bg-gray-200'}`}>
                  {plan.cta}
                </Link>
              </div>
            ))}
          </div>
          <p className="text-center text-sm text-gray-400 dark:text-gray-500 mt-8">
            Need a full AI Department?{' '}
            <Link href="/#pricing" className="underline underline-offset-2 hover:text-[#1a1a1a] dark:hover:text-white transition">See department plans →</Link>
          </p>
        </div>
      </section>

      {/* ── FAQ ── */}
      <section className="py-20 sm:py-28 px-4 sm:px-6">
        <div className="max-w-3xl mx-auto">
          <div className="mb-14 text-center">
            <div className="text-[11px] font-semibold tracking-widest uppercase text-gray-400 mb-3">FAQ</div>
            <h2 style={serifStyle} className="text-4xl sm:text-5xl text-[#1a1a1a] dark:text-white">Common questions</h2>
          </div>
          <div className="divide-y divide-[#E8E8E4] dark:divide-gray-800">
            {FAQ.map((item, i) => (
              <div key={i} className="py-5">
                <button onClick={() => setOpenFaq(openFaq === i ? null : i)} className="w-full flex items-center justify-between gap-4 text-left">
                  <span className="text-sm font-semibold text-[#1a1a1a] dark:text-white leading-relaxed">{item.q}</span>
                  <svg className={`w-4 h-4 flex-shrink-0 text-gray-400 transition-transform ${openFaq === i ? 'rotate-180' : ''}`} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}><path strokeLinecap="round" strokeLinejoin="round" d="M19 9l-7 7-7-7"/></svg>
                </button>
                {openFaq === i && (
                  <p className="mt-3 text-sm text-gray-500 dark:text-gray-400 leading-relaxed">{item.a}</p>
                )}
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── CTA ── */}
      <section className="py-20 sm:py-28 px-4 sm:px-6 bg-[#1a1a1a] dark:bg-white">
        <div className="max-w-3xl mx-auto text-center">
          <h2 style={serifStyle} className="text-4xl sm:text-5xl text-white dark:text-[#1a1a1a] mb-5">
            Deploy your agent today
          </h2>
          <p className="text-gray-400 dark:text-gray-600 text-base leading-relaxed mb-8 max-w-xl mx-auto">
            14-day free trial. Your agent goes live in hours. No technical setup, no long contracts.
          </p>
          <div className="flex flex-wrap gap-3 justify-center">
            <Link href="/login?redirect=/dashboard" className="bg-white dark:bg-[#1a1a1a] text-[#1a1a1a] dark:text-white px-8 py-3.5 rounded-full text-sm font-semibold hover:bg-gray-100 dark:hover:bg-gray-800 transition">
              Start Free Trial
            </Link>
            <Link href="/" className="border border-gray-700 dark:border-gray-300 text-gray-300 dark:text-gray-600 px-8 py-3.5 rounded-full text-sm font-semibold hover:border-white dark:hover:border-[#1a1a1a] hover:text-white dark:hover:text-[#1a1a1a] transition">
              View AI Department →
            </Link>
          </div>
        </div>
      </section>

      {/* ── Footer ── */}
      <footer className="py-12 px-4 sm:px-6 border-t border-[#E8E8E4] dark:border-gray-800">
        <div className="max-w-7xl mx-auto flex flex-col sm:flex-row items-center justify-between gap-4">
          <Logo location="dashboard" />
          <div className="flex gap-6">
            <Link href="/" className="text-xs text-gray-400 hover:text-[#1a1a1a] dark:hover:text-white transition">AI Department</Link>
            <Link href="/ai-employee-marketplace" className="text-xs text-gray-400 hover:text-[#1a1a1a] dark:hover:text-white transition">Marketplace</Link>
            <Link href="/ai-agent" className="text-xs text-gray-400 hover:text-[#1a1a1a] dark:hover:text-white transition">Single Agent</Link>
            <Link href="/privacy" className="text-xs text-gray-400 hover:text-[#1a1a1a] dark:hover:text-white transition">Privacy</Link>
          </div>
          <p className="text-xs text-gray-400">© {new Date().getFullYear()} Preplix. All rights reserved.</p>
        </div>
      </footer>
    </div>
  )
}
